import { useRouter } from 'next/router';
import { graphql } from 'react-relay';
import { usePromiseMutation } from '@sparknz/journeys-common';
import { useSnackbar } from '@sparknz/spark-pattern-library';

import { useInitiateCheckoutMutation } from '__generated__/useInitiateCheckoutMutation.graphql';
import { CHECKOUT_PAGE } from 'shared/constants';
import { useCartCookie, useSegment } from 'shared/hooks';
import { Cart } from 'shared/types/cart';
import { getPath } from 'shared/utils';
import { useCartAction } from '../useCartAction';
import {
  trackInitiateCheckoutFail,
  trackInitiateCheckoutSuccess,
} from './utils';

const initiateCheckoutMutation = graphql`
  mutation useInitiateCheckoutMutation($input: InitiateCheckoutInput!) {
    initiateCheckout(input: $input) {
      message
      code
      success
    }
  }
`;

interface HandleInitiateCheckoutErrorParameter {
  code: number;
  message: string;
}

interface UseInitiateCheckoutProps {
  cart: Cart;
}

const ERROR_MESSAGES_LOOKUP = [
  {
    errorCode: 4221,
    message: `Sorry - you can only have up to $10,000 worth of items in your cart. You'll need to remove some items and try again.`,
  },
  {
    errorCode: 4240,
    message: `Items in your cart need to be available online, or collected from the same store. Please edit your cart, and consider separate orders for items from different stores.`,
  },
  {
    errorCode: 4234,
    message: `Sorry - you can only have up to $4999 worth of Gift Cards in your cart. You'll need to remove some items and try again.`,
  },
];
const DEFAULT_ERROR_MESSAGE = 'Something went wrong';

function resolveErrorMessage(code: number) {
  const messageItem = ERROR_MESSAGES_LOOKUP.find(
    ({ errorCode }) => errorCode === code,
  );
  return messageItem?.message || DEFAULT_ERROR_MESSAGE;
}

export default function useInitiateCheckout({
  cart,
}: UseInitiateCheckoutProps) {
  const mutateInitiateCheckout =
    usePromiseMutation<useInitiateCheckoutMutation>(initiateCheckoutMutation);

  const router = useRouter();
  const { showSnackbar } = useSnackbar();
  const { channel, isBusiness } = useSegment();
  const { cartId } = useCartCookie();
  const { stockInfo } = useCartAction();

  function handleInitiateCheckoutError({
    code,
    message,
  }: HandleInitiateCheckoutErrorParameter) {
    showSnackbar({
      text: resolveErrorMessage(code),
      variant: 'error',
    });
    trackInitiateCheckoutFail({
      internalErrorCode: code,
      internalErrorMessage: message ?? '',
    });
  }

  async function initiateCheckout() {
    try {
      const {
        initiateCheckout: { success, code, message },
      } = await mutateInitiateCheckout({
        input: { cartId, channel },
      });

      if (!success) {
        handleInitiateCheckoutError({ code, message });
        return;
      }

      if (cart) {
        trackInitiateCheckoutSuccess({ cart, isBusiness, stockInfo });
      }
      router.push({
        pathname: getPath(CHECKOUT_PAGE, channel),
      });
    } catch {
      handleInitiateCheckoutError({
        code: 500,
        message: '',
      });
    }
  }

  return {
    initiateCheckout,
  };
}
